import type { ReactElement } from 'react';
import { useTranslation } from '@govard/i18n-core/react';
import { Activity, AlertTriangle, Bot, CheckCircle2, History, Zap } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Typography } from 'antd';

import { WidgetContainer, type WidgetContainerProps } from './WidgetContainer.js';
import type { ActivityScopeType } from './types.js';

const { Text } = Typography;

export interface RecentActivityItem {
  id?: string;
  dealId?: string;
  title?: string;
  message?: string;
  timestamp?: string;
  scopeType?: ActivityScopeType;
  type?: ActivityScopeType;
}

export interface RecentActivityWidgetProps {
  items?: readonly RecentActivityItem[];
  className?: string;
  testId?: string;
  loading?: boolean;
  onItemClick?: (item: RecentActivityItem) => void;
}

function timeAgo(
  timestamp: string,
  t: (key: string, params?: Record<string, string | number>) => string,
): string {
  const diff = Date.now() - new Date(timestamp).getTime();
  const minutes = Math.floor(diff / 60_000);

  if (minutes < 1) {
    return t('crm.dashboard.time.just_now');
  }

  if (minutes < 60) {
    return t('crm.dashboard.time.minutes_ago', { count: minutes });
  }

  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return t('crm.dashboard.time.hours_ago', { count: hours });
  }

  return t('crm.dashboard.time.days_ago', { count: Math.floor(hours / 24) });
}

const ACTIVITY_ICONS: Record<string, LucideIcon> = {
  session: Bot,
  task: CheckCircle2,
  error: AlertTriangle,
  system: Activity,
  pipeline: Zap,
};

function getActivityColor(scopeType: string): string {
  switch (scopeType) {
    case 'session':
      return 'var(--ant-color-primary)';
    case 'task':
      return 'var(--ant-color-success)'; // success green
    case 'error':
      return 'var(--ant-color-error)'; // error red
    case 'pipeline':
      return 'var(--ant-color-primary)'; // warning amber
    default:
      return 'var(--ant-color-info)'; // info blue
  }
}

export function RecentActivityWidget({
  items = [],
  className,
  testId,
  loading = false,
  onItemClick,
}: RecentActivityWidgetProps): ReactElement {
  const { t } = useTranslation();

  const widgetContainerProps = {
    title: t('crm.dashboard.activity.title'),
    icon: History,
    minHeight: 300,
    noPadding: true,
    ...(className ? { className } : {}),
    ...(testId ? { testId } : {}),
    headerRight: (
      <span
        style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--ant-color-text-tertiary)' }}
      >
        {t('crm.dashboard.activity.events_count', { count: items.length })}
      </span>
    ),
  } satisfies Omit<WidgetContainerProps, 'children'>;

  return (
    <WidgetContainer {...widgetContainerProps}>
      {loading ? (
        <div className="animate-pulse p-3.5 flex flex-col gap-1.5">
          {[1, 2, 3, 4].map((index) => (
            <div
              key={index}
              className="crm-radius-s h-10"
              style={{ background: 'var(--g-fill, #f5f1eb)' }}
            />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div
          className="flex items-center justify-center gap-2"
          style={{ padding: '18px 16px', opacity: 0.6 }}
        >
          <History size={16} style={{ color: 'var(--ant-color-text-tertiary)' }} />
          <span style={{ fontSize: 12, color: 'var(--ant-color-text-tertiary)' }}>
            {t('crm.dashboard.activity.empty')}
          </span>
        </div>
      ) : (
        <ul className="m-0 list-none p-0">
          {items.map((item) => {
            const scopeType = item.scopeType ?? item.type ?? 'system';
            const Icon = ACTIVITY_ICONS[scopeType] ?? Activity;
            const color = getActivityColor(scopeType);

            return (
              <li key={item.id ?? `${item.title ?? ''}-${item.timestamp ?? ''}`}>
                <button
                  type="button"
                  onClick={onItemClick ? () => onItemClick(item) : undefined}
                  className="transition-colors text-left border-0 flex w-full items-start gap-3 crm-hover-fill"
                  style={{
                    cursor: onItemClick ? 'pointer' : 'default',
                    opacity: onItemClick ? 1 : 0.8,
                    padding: '6px 14px',
                    minHeight: 42,
                    borderBottom: '1px solid var(--ant-color-border-secondary)',
                    background: 'transparent',
                  }}
                >
                  <div
                    className="crm-radius-s flex items-center justify-center shrink-0 mt-1"
                    style={{
                      width: 26,
                      height: 26,
                      background: color.startsWith('var(')
                        ? `color-mix(in srgb, ${color} 10%, transparent)`
                        : `${color}12`,
                      color,
                    }}
                  >
                    <Icon size={13} style={{ color }} />
                  </div>
                  <div className="flex-1 min-w-0 py-1">
                    <Text
                      strong
                      className="truncate block"
                      style={{ fontSize: 12.5, color: 'var(--ant-color-text)', lineHeight: 1.3 }}
                    >
                      {item.title ?? item.message ?? t('crm.dashboard.activity.fallback_title')}
                    </Text>
                    {item.message && item.title ? (
                      <Text
                        type="secondary"
                        className="block mt-0.5"
                        style={{
                          fontSize: 11,
                          lineHeight: 1.4,
                          color: 'var(--ant-color-text-secondary)',
                        }}
                      >
                        {item.message}
                      </Text>
                    ) : null}
                  </div>
                  <span
                    style={{
                      fontFamily: 'monospace',
                      fontSize: 11,
                      color: 'var(--ant-color-text-tertiary)',
                      whiteSpace: 'nowrap',
                      marginTop: 4,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {item.timestamp ? timeAgo(item.timestamp, t) : ''}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </WidgetContainer>
  );
}
